import { ArrowLeft, ArrowRight } from "../Icons/Arrows";
import { CircleFull, CircleEmpty } from "../Icons/Circles";
import React, { useState, useEffect, useCallback } from "react";
import { useSwipeActions } from "../Gestures/SwipeProvider";

export enum JustifyEnum {
	left = "justify-left",
	right = "justify-end",
	center = "justify-center"
}
export enum PlacementEnum {
	top,
	bottom
}
interface CarouselProps<T> {
	slides: T[];
	onRenderSlide: (slide: T, index: number) => React.ReactNode;
	className?: string;
	justification?: JustifyEnum;
	placement?: PlacementEnum;
}

//TODO: (Optional) Add AutoTransistion feature?
const Carousel = <T,>({
	slides,
	onRenderSlide,
	className = "",
	justification: justifacation = JustifyEnum.right,
	placement = PlacementEnum.bottom
}: CarouselProps<T>) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const { registerSwipeHandlers } = useSwipeActions();

	const goToSlide = (index: number) => {
		// console.log(`activeIndex: ${activeIndex} index: ${index}`);
		// Handle wrapping around when going past bounds
		if (index < 0) {
			setActiveIndex(slides.length - 1);
		} else if (slides.length <= index) {
			setActiveIndex(0);
		} else {
			setActiveIndex(index);
		}
	};

	const handleLeftSwipe = useCallback(() => {
		goToSlide(activeIndex + 1);
		// eslint-disable-next-line
	}, [activeIndex]);

	const handleRightSwipe = useCallback(() => {
		goToSlide(activeIndex - 1);
		// eslint-disable-next-line
	}, [activeIndex]);

	useEffect(() => {
		registerSwipeHandlers(handleLeftSwipe, handleRightSwipe);
	}, [registerSwipeHandlers, handleLeftSwipe, handleRightSwipe]);

	const goToPrevious = () => {
		goToSlide(activeIndex - 1);
	};

	const goToNext = () => {
		goToSlide(activeIndex + 1);
	};

	// Don't render if there are no slides
	if (slides.length === 0) {
		return null;
	}

	return (
		<div className={` ${className}`}>
			{/* User Rendered slide - the current slide if the carosusel placement is bottom*/}
			{placement === PlacementEnum.bottom && (
				<div key={activeIndex} className="w-full mb-3 transition-all">
					{onRenderSlide(slides[activeIndex], activeIndex)}
				</div>
			)}

			{/* Navigation controls */}
			<div className={`flex items-center ${justifacation}`}>
				{/* Left arrow button */}
				<button
					onClick={goToPrevious}
					className="mr-2 text-primary-active hover:text-primary-inactive transition-all"
					aria-label="Previous slide"
				>
					{ArrowLeft()}
				</button>

				{/* Pagination dots */}
				{slides.map((_, index) => (
					<button
						key={index}
						onClick={() => goToSlide(index)}
						className={`mx-0.5 transition-all ${
							index === activeIndex
								? "text-primary-active hover:text-primary-inactive"
								: "text-primary-inactive hover:text-primary-active"
						}`}
						aria-label={`Go to slide ${index + 1}`}
						aria-current={index === activeIndex ? "true" : "false"}
					>
						{index === activeIndex ? CircleFull() : CircleEmpty()}
					</button>
				))}

				{/* Right arrow button */}
				<button
					onClick={goToNext}
					className="ml-2 text-primary-active hover:text-primary-inactive transition-all"
					aria-label="Next slide"
				>
					{ArrowRight()}
				</button>
			</div>

			{/* User Rendered slide - the current slide if the carosusel placement is top*/}
			{placement === PlacementEnum.top && (
				<div key={activeIndex} className="w-full mt-3 transition-all">
					{onRenderSlide(slides[activeIndex], activeIndex)}
				</div>
			)}
		</div>
	);
};

export default Carousel;
